/**
 * Form Draft Service — DB-first persistence for in-progress form input.
 *
 * The product rule is: the truth lives in the database, never in Zustand or
 * localStorage. This service extends that rule to *unsaved* form input. While a
 * user fills in a wizard or a long form, their keystrokes are autosaved to the
 * `form_drafts` table (see migration 524). On reload the form rehydrates from the
 * DB — so a refresh never loses work, and the DB remains the single source of truth.
 *
 * A draft is a private, per-user scratchpad for one form instance, identified by a
 * stable `draftKey`. It is NOT canonical data: once the form commits to its real
 * entity, the draft is discarded.
 *
 * All operations are best-effort: if Supabase is unavailable or the user is not
 * authenticated, they no-op (returning null / false) so the form keeps working.
 */

import { getSafeSupabaseClient, getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { requireAuthUser } from '../../lib/requireAuthUser';

const TABLE_NAME = 'form_drafts';

export interface FormDraft<T = unknown> {
  id: string;
  draftKey: string;
  workspaceId: string | null;
  payload: T;
  updatedAt: string;
}

interface DraftScope {
  /** Stable identifier for the form instance, e.g. 'submit-idea'. */
  draftKey: string;
  /**
   * Workspace the draft belongs to. Defaults to the current workspace. Pass
   * `null` explicitly for forms that run before a workspace is chosen.
   */
  workspaceId?: string | null;
}

/**
 * Resolve the authenticated user id. RLS requires `user_id = auth.uid()`, so we
 * read the id straight from the auth session rather than trusting client state.
 */
async function resolveUserId(): Promise<string | null> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return null;
  const { user } = await requireAuthUser(supabase);
  return user?.id ?? null;
}

function resolveWorkspaceId(scope: DraftScope): string | null {
  return scope.workspaceId !== undefined ? scope.workspaceId : getCurrentWorkspaceId();
}

/**
 * Load the current draft for a form instance, or null if none exists.
 */
export async function loadFormDraft<T = unknown>(
  scope: DraftScope
): Promise<FormDraft<T> | null> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return null;

  const userId = await resolveUserId();
  if (!userId) return null;

  const workspaceId = resolveWorkspaceId(scope);

  let query = supabase
    .from(TABLE_NAME)
    .select('id, draft_key, workspace_id, payload, updated_at')
    .eq('user_id', userId)
    .eq('draft_key', scope.draftKey);

  query = workspaceId == null
    ? query.is('workspace_id', null)
    : query.eq('workspace_id', workspaceId);

  const { data, error } = await query.maybeSingle();

  if (error) {
    console.warn('[FormDraftService] loadFormDraft failed:', error.message);
    return null;
  }
  if (!data) return null;

  return {
    id: data.id as string,
    draftKey: data.draft_key as string,
    workspaceId: (data.workspace_id as string | null) ?? null,
    payload: data.payload as T,
    updatedAt: data.updated_at as string,
  };
}

/**
 * Save (insert or update) the draft for a form instance. Returns the row id, or
 * null on failure. Manual upsert (no `onConflict`) keeps the expression-based
 * unique index happy and lets us recover from races by re-updating.
 */
export async function saveFormDraft<T = unknown>(
  scope: DraftScope,
  payload: T
): Promise<string | null> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return null;

  const userId = await resolveUserId();
  if (!userId) return null;

  const workspaceId = resolveWorkspaceId(scope);

  const existing = await loadFormDraft(scope);
  if (existing) {
    const { error } = await supabase
      .from(TABLE_NAME)
      .update({ payload })
      .eq('id', existing.id);
    if (error) {
      console.warn('[FormDraftService] update draft failed:', error.message);
      return null;
    }
    return existing.id;
  }

  const { data, error } = await supabase
    .from(TABLE_NAME)
    .insert({
      user_id: userId,
      workspace_id: workspaceId,
      draft_key: scope.draftKey,
      payload,
    })
    .select('id')
    .single();

  if (error) {
    // A concurrent insert may have won the unique index race; fall back to update.
    const retry = await loadFormDraft(scope);
    if (retry) {
      const { error: updErr } = await supabase
        .from(TABLE_NAME)
        .update({ payload })
        .eq('id', retry.id);
      if (!updErr) return retry.id;
    }
    console.warn('[FormDraftService] insert draft failed:', error.message);
    return null;
  }

  return (data?.id as string) ?? null;
}

/**
 * Delete the draft for a form instance. Call this once the form has committed its
 * canonical entity (so the scratchpad doesn't linger and re-hydrate stale input).
 */
export async function discardFormDraft(scope: DraftScope): Promise<boolean> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return false;

  const userId = await resolveUserId();
  if (!userId) return false;

  const workspaceId = resolveWorkspaceId(scope);

  let query = supabase
    .from(TABLE_NAME)
    .delete()
    .eq('user_id', userId)
    .eq('draft_key', scope.draftKey);

  query = workspaceId == null
    ? query.is('workspace_id', null)
    : query.eq('workspace_id', workspaceId);

  const { error } = await query;
  if (error) {
    console.warn('[FormDraftService] discard draft failed:', error.message);
    return false;
  }
  return true;
}
